import { PropsWithChildren, createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAccount } from "wagmi";
import { ethers } from "ethers";

export interface AuthUser {
  address: string;
  hasWallet: boolean;
}

export interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  token: string | null;
  login: (address: string, signature: string, message?: string) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  makeAuthenticatedRequest: (url: string, options?: RequestInit) => Promise<Response | undefined>;
}

const context = createContext<AuthContextType | null>(null);

// Cookie utilities
const setCookie = (name: string, value: string, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  // Use Lax for development to allow cookie setting
  const isProduction = window.location.protocol === "https:";
  const sameSite = isProduction ? "Strict" : "Lax";
  const secure = isProduction ? ";Secure" : "";

  const cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=${sameSite}${secure}`;

  console.log("Setting cookie:", cookieString);
  document.cookie = cookieString;

  // Verify cookie was set
  const testCookie = getCookie(name);
  console.log(
    "Cookie verification - set:",
    value ? value.substring(0, 20) + "..." : "empty",
    "retrieved:",
    testCookie ? testCookie.substring(0, 20) + "..." : "null"
  );
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const getAllCookies = () => {
  console.log("All cookies:", document.cookie);
  return document.cookie;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  console.log("Deleted cookie:", name);
};

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "https://backend-dev-api.backend.perpx.ai";

export function AuthContextProvider({ children }: PropsWithChildren) {
  const { address: walletAddress, isConnected } = useAccount();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [token, setToken] = useState<string | null>(() => getCookie("authToken"));
  const [lastWalletConnectionTime, setLastWalletConnectionTime] = useState<number>(0);
  const ongoingAuthRef = useRef<string | null>(null); // Track ongoing auth by wallet address
  const authAttemptedRef = useRef<string | null>(null); // Track if we've attempted auth for current wallet

  // Generate login message
  const generateLoginMessage = useCallback((address: string): string => {
    return `PerpX requires a one-time message signature.\n\nThis helps us authenticate your wallet with our backend infrastructure, maintain session continuity, and ensure smooth order execution.\n\n🔒 No transactions or gas fees are involved.\n🔑 Your private keys remain fully secure and are never shared.\n\nPlease review and sign the message to continue.\n\nAddress: ${address}\n\nTimestamp: ${new Date().toISOString()}`;
  }, []);

  // Sign message with MetaMask
  const signMessage = useCallback(
    async (message: string): Promise<string> => {
      if (!window.ethereum) {
        throw new Error("MetaMask not found");
      }

      if (!walletAddress) {
        throw new Error("No wallet connected");
      }

      try {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const signature = await signer.signMessage(message);
        return signature;
      } catch (error) {
        console.error("Message signing error:", error);
        throw error;
      }
    },
    [walletAddress]
  );

  // Redirect to login (in this case, we'll trigger wallet connection)
  const redirectToLogin = useCallback(() => {
    console.log("Token expired, clearing auth state");
    deleteCookie("authToken");
    setToken(null);
    setUser(null);
    setAuthenticated(false);
  }, []);

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    try {
      const authToken = getCookie("authToken");
      if (!authToken) {
        redirectToLogin();
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/auth/verify`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data.user);
        setAuthenticated(true);
        setToken(authToken);
      } else if (response.status === 403) {
        // Token expired or invalid
        deleteCookie("authToken");
        redirectToLogin();
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      redirectToLogin();
    }
  }, [redirectToLogin]);

  // Login function
  const login = useCallback(async (address: string, signature: string, message?: string) => {
    setIsAuthenticating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address,
          signature,
          message,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Login failed" }));
        throw new Error(error.message || "Login failed");
      }

      const data = await response.json();

      if (data.status && data.data) {
        const authToken = data.data.token;
        console.log("AuthContext: Login successful, setting cookie with token:", authToken.substring(0, 20) + "...");
        setCookie("authToken", authToken);
        setToken(authToken);
        setUser({
          address: data.data.user.address,
          hasWallet: data.data.user.hasWallet,
        });
        setAuthenticated(true);
        console.log("AuthContext: Authentication state updated");
      } else {
        throw new Error(data.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  // Make authenticated request with automatic token refresh
  const makeAuthenticatedRequest = useCallback(
    async (url: string, options: RequestInit = {}, retryCount = 0): Promise<Response | undefined> => {
      try {
        const authToken = getCookie("authToken");

        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (response.status === 403 && retryCount === 0) {
          const data = await response.json();
          if (data.message === "Invalid or expired token") {
            console.log("AuthContext: Token expired, attempting automatic re-authentication...");

            // Only attempt re-auth if we have a wallet and we're not already authenticating
            if (walletAddress && !isAuthenticating && ongoingAuthRef.current !== walletAddress) {
              try {
                console.log("AuthContext: Re-authenticating with wallet:", walletAddress);
                ongoingAuthRef.current = walletAddress;
                setIsAuthenticating(true);

                const message = generateLoginMessage(walletAddress);
                const signature = await signMessage(message);
                await login(walletAddress, signature, message);

                console.log("AuthContext: Re-authentication successful, retrying request...");

                // Retry the original request with new token
                const newToken = getCookie("authToken");
                if (newToken) {
                  const retryResponse = await fetch(url, {
                    ...options,
                    headers: {
                      ...options.headers,
                      Authorization: `Bearer ${newToken}`,
                    },
                  });

                  ongoingAuthRef.current = null;
                  setIsAuthenticating(false);
                  return retryResponse;
                } else {
                  console.error("AuthContext: No new token after re-authentication");
                  ongoingAuthRef.current = null;
                  setIsAuthenticating(false);
                  redirectToLogin();
                  return;
                }
              } catch (reAuthError) {
                console.error("AuthContext: Re-authentication failed:", reAuthError);
                ongoingAuthRef.current = null;
                setIsAuthenticating(false);
                // Fall back to clearing auth state
                redirectToLogin();
                return;
              }
            } else {
              if (isAuthenticating) {
                console.log("AuthContext: Already authenticating, skipping re-auth attempt");
              } else {
                console.log("AuthContext: No wallet available for re-authentication, clearing auth state");
                // No wallet available, clear auth state
                redirectToLogin();
              }
              return;
            }
          }
        }

        return response;
      } catch (error) {
        console.error("Request failed:", error);
      }
    },
    [walletAddress, isAuthenticating, generateLoginMessage, signMessage, login, redirectToLogin]
  );

  // Logout function
  const logout = useCallback(() => {
    deleteCookie("authToken");
    setToken(null);
    setUser(null);
    setAuthenticated(false);
    // Clear auth attempt tracking so user can authenticate again
    authAttemptedRef.current = null;
  }, []);

  // Auto-authenticate when wallet connects
  useEffect(() => {
    console.log("AuthContext: Wallet effect triggered", {
      isConnected,
      walletAddress,
      user: !!user,
      isAuthenticating,
      isAuthenticated,
    });

    const autoAuthenticate = async () => {
      console.log("AuthContext: Checking auto-auth conditions:", {
        isConnected,
        walletAddress,
        user: !!user,
        isAuthenticating,
        isAuthenticated,
      });

      // Only auto-authenticate if:
      // 1. Wallet is connected
      // 2. We have a wallet address
      // 3. Not currently authenticating
      // 4. Either no user at all, or user exists but address doesn't match (address changed)
      const isNewConnection = !user;
      const isAddressMismatch = user && walletAddress && walletAddress.toLowerCase() !== user.address.toLowerCase();

      // For address mismatches, wait a bit to ensure connection is stable
      // But allow immediate auth for completely new connections (lastWalletConnectionTime = 0)
      const timeSinceConnection = Date.now() - lastWalletConnectionTime;
      const isConnectionStable = lastWalletConnectionTime === 0 || timeSinceConnection > 500;

      const shouldAutoAuth =
        isConnected &&
        walletAddress &&
        !isAuthenticating &&
        !token &&
        (isNewConnection || (isAddressMismatch && isConnectionStable)) &&
        authAttemptedRef.current !== walletAddress; // Only if we haven't tried this wallet yet

      console.log("AuthContext: Should auto-auth?", shouldAutoAuth, {
        reason: isNewConnection
          ? "new connection"
          : isAddressMismatch
            ? isConnectionStable
              ? "address mismatch"
              : `address mismatch, waiting for stability (${timeSinceConnection}ms ago)`
            : "already authenticated",
        timeSinceConnection,
        isConnectionStable,
        isNewConnection,
        isAddressMismatch,
        hasAuthToken: !!token,
        alreadyAttemptedForWallet: authAttemptedRef.current === walletAddress,
      });

      if (shouldAutoAuth) {
        // Prevent duplicate authentication attempts for the same wallet
        if (ongoingAuthRef.current === walletAddress) {
          console.log("AuthContext: Authentication already in progress for wallet:", walletAddress);
          return;
        }

        console.log("AuthContext: Conditions met, auto-authenticating wallet:", walletAddress);
        authAttemptedRef.current = walletAddress; // Mark that we've attempted auth for this wallet
        ongoingAuthRef.current = walletAddress;
        setIsAuthenticating(true);

        try {
          const message = generateLoginMessage(walletAddress);
          const signature = await signMessage(message);
          await login(walletAddress, signature, message);
          console.log("Auto-authentication successful");
        } catch (error) {
          console.error("Auto-authentication failed:", error);
          // Don't show error, user can manually authenticate later
        } finally {
          ongoingAuthRef.current = null;
          setIsAuthenticating(false);
        }
      } else {
        console.log("AuthContext: Auto-auth conditions not met");
        // If conditions are not met but we have a different wallet, clear any ongoing auth
        if (ongoingAuthRef.current && ongoingAuthRef.current !== walletAddress) {
          console.log("AuthContext: Clearing stale auth ref for different wallet");
          ongoingAuthRef.current = null;
        }
      }
    };

    // Delay to ensure all providers are ready, but only run once per wallet connection
    const timeoutId = setTimeout(autoAuthenticate, 1500); // Increased delay to avoid conflicts
    return () => clearTimeout(timeoutId);
  }, [isConnected, walletAddress, user?.address, isAuthenticating, token, generateLoginMessage, signMessage, login]);

  // Track wallet connection time and auto-logout if wallet disconnects and user addresses don't match
  useEffect(() => {
    if (isConnected && walletAddress) {
      setLastWalletConnectionTime(Date.now());
      console.log("Wallet connected at:", new Date().toISOString());
    } else if (!isConnected) {
      // Clear auth attempt tracking when wallet disconnects
      authAttemptedRef.current = null;
      console.log("Wallet disconnected, cleared auth attempt tracking");
    }

    if (user && walletAddress && user.address.toLowerCase() !== walletAddress.toLowerCase()) {
      console.log("Wallet address changed, logging out");
      // Clear auth attempt tracking for the old wallet
      authAttemptedRef.current = null;
      logout();
    }
  }, [isConnected, walletAddress, user, logout]);

  // Check auth status on app initialization
  useEffect(() => {
    console.log("AuthContext: Initializing...");
    getAllCookies(); // Debug: show all cookies
    const existingToken = getCookie("authToken");
    console.log("AuthContext: Existing token in cookie:", existingToken ? "YES" : "NO");
    checkAuthStatus();
  }, [checkAuthStatus]);

  // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
  const value = {
    user,
    isAuthenticated,
    isAuthenticating,
    token,
    login,
    logout,
    checkAuthStatus,
    makeAuthenticatedRequest,
  };

  return <context.Provider value={value}>{children}</context.Provider>;
}

export const useAuth = () => {
  const authContext = useContext(context);
  if (!authContext) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return authContext;
};
